import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import {
  signIn,
  confirmSignUp,
  resendSignUpCode,
  signUp,
  resetPassword,
  confirmResetPassword,
} from "aws-amplify/auth";
import Header from "../Header";

import "./Auth.css";

const Login: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isDevBypass } = useAuth();
  const navigate = useNavigate();
  const isDevelopment = import.meta.env.DEV;

  // Check if we're on allowed domains
  const currentHostname = window.location.hostname;
  const allowedDevDomains = [
    "dev.gesslargardens.com",
    "stage.gesslargardens.com",
  ];
  const isAllowedDomain = allowedDevDomains.includes(currentHostname);
  const isCloudFrontDomain = currentHostname.endsWith("cloudfront.net");

  // Allow dev message in development mode OR on allowed domains OR on CloudFront domains
  const shouldShowDevMessage =
    isDevelopment || isAllowedDomain || isCloudFrontDomain;

  // Determine environment label
  let envLabel = "Dev";
  if (isAllowedDomain) {
    envLabel = currentHostname.startsWith("dev.") ? "Dev" : "Staging";
  } else if (isCloudFrontDomain) {
    envLabel = "Cloud";
  }

  useEffect(() => {
    // Redirect to dashboard if already authenticated
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Local UI state
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [code, setCode] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [mode, setMode] = React.useState<
    "signIn" | "signUp" | "confirm" | "reset" | "resetConfirm"
  >("signIn");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [infoMessage, setInfoMessage] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [showResetNewPassword, setShowResetNewPassword] = React.useState(false);

  const switchMode = (nextMode: typeof mode) => {
    setMode(nextMode);
    setErrorMessage("");
    setInfoMessage("");
    setCode("");
    setIsSubmitting(false);
    // Only clear password fields when switching contexts
    if (nextMode !== "resetConfirm") {
      setNewPassword("");
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      await signIn({ username: email, password });
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      const name = err?.name || "Error";
      // Handle common Cognito errors
      if (name === "UserNotConfirmedException") {
        try {
          await resendSignUpCode({ username: email });
        } catch {}
        switchMode("confirm");
      } else if (name === "PasswordResetRequiredException") {
        switchMode("reset");
      } else if (name === "NotAuthorizedException") {
        setErrorMessage(t("auth.login.errors.incorrectCredentials"));
      } else if (name === "UserNotFoundException") {
        setErrorMessage(t("auth.login.errors.accountNotFound"));
      } else {
        setErrorMessage(err?.message || t("auth.login.errors.signInFailed"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      // Now sign in
      await signIn({ username: email, password });
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      setErrorMessage(err?.message || t("auth.verify.errors.confirmationFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      await resetPassword({ username: email });
      switchMode("resetConfirm");
    } catch (err: any) {
      setErrorMessage(err?.message || t("auth.login.errors.resetStartFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      // After reset, sign in
      await signIn({ username: email, password: newPassword });
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      setErrorMessage(err?.message || t("auth.login.errors.resetFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      if (password !== confirmPassword) {
        setIsSubmitting(false);
        setErrorMessage(t("auth.login.errors.passwordsDoNotMatch"));
        return;
      }
      await signUp({
        username: email,
        password,
        options: { userAttributes: { email } },
      });
      // Move to confirm flow
      switchMode("confirm");
      setInfoMessage(t("auth.verify.success.codeSent"));
    } catch (err: any) {
      const name = err?.name || "Error";
      if (name === "UsernameExistsException") {
        // Stay on create account and show an explicit error
        setErrorMessage(t("auth.login.errors.accountExists"));
      } else {
        setErrorMessage(err?.message || t("auth.login.errors.signUpFailed"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <Header />
      <div className="auth-container">
        <div className="auth-card">
          <h2 className="auth-title">
            {mode === "signIn"
              ? t("auth.login.title")
              : mode === "signUp"
              ? t("auth.login.createAccount")
              : mode === "confirm"
              ? t("auth.verify.title")
              : mode === "reset"
              ? t("auth.login.resetPassword")
              : t("auth.login.setNewPassword")}
          </h2>
          <p className="auth-description">
            {mode === "signIn"
              ? t("auth.login.description")
              : mode === "signUp"
              ? t("auth.login.createAccountDescription")
              : mode === "confirm"
              ? t("auth.verify.description")
              : mode === "reset"
              ? t("auth.login.resetDescription")
              : t("auth.login.setNewPasswordDescription")}
          </p>

          {infoMessage && (
            <div className="auth-success-message" role="status">
              {infoMessage}
            </div>
          )}
          {errorMessage && (
            <div className="auth-error-message" role="alert">
              {errorMessage}
            </div>
          )}

          {mode === "signIn" && (
            <form className="auth-form" onSubmit={handleSignIn}>
              <div className="form-group">
                <label htmlFor="email">{t("auth.login.emailLabel")}</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("auth.login.emailPlaceholder")}
                  autoComplete="username"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">{t("auth.login.passwordLabel")}</label>
                <div className="password-wrapper">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("auth.login.passwordPlaceholder")}
                    autoComplete="current-password"
                    required
                    disabled={isSubmitting}
                    style={{ paddingRight: "56px" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="password-toggle"
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <i
                      className={`fas ${
                        showPassword ? "fa-eye-slash" : "fa-eye"
                      }`}
                      aria-hidden="true"
                    ></i>
                    <span className="sr-only">
                      {showPassword ? t("auth.login.hidePassword") : t("auth.login.showPassword")}
                    </span>
                  </button>
                </div>
              </div>
              <button
                className="auth-button auth-button-primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? t("auth.login.signingIn") : t("auth.login.signIn")}
              </button>
              <div className="auth-actions">
                <button
                  type="button"
                  className="auth-button auth-button-secondary"
                  onClick={() => switchMode("reset")}
                  disabled={isSubmitting}
                >
                  {t("auth.login.forgotPassword")}
                </button>
                <button
                  type="button"
                  className="auth-button auth-button-secondary"
                  onClick={() => switchMode("signUp")}
                  disabled={isSubmitting}
                >
                  {t("auth.login.createAccount")}
                </button>
              </div>
            </form>
          )}

          {mode === "signUp" && (
            <form className="auth-form" onSubmit={handleSignUp}>
              <div className="form-group">
                <label htmlFor="signupEmail">{t("auth.login.emailLabel")}</label>
                <input
                  id="signupEmail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("auth.login.emailPlaceholder")}
                  autoComplete="username"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="signupPassword">{t("auth.login.passwordLabel")}</label>
                <div className="password-wrapper">
                  <input
                    id="signupPassword"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("auth.login.choosePassword")}
                    autoComplete="new-password"
                    required
                    disabled={isSubmitting}
                    style={{ paddingRight: "56px" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="password-toggle"
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <i
                      className={`fas ${
                        showPassword ? "fa-eye-slash" : "fa-eye"
                      }`}
                      aria-hidden="true"
                    ></i>
                    <span className="sr-only">
                      {showPassword ? t("auth.login.hidePassword") : t("auth.login.showPassword")}
                    </span>
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="signupPasswordConfirm">{t("auth.login.confirmPassword")}</label>
                <div className="password-wrapper">
                  <input
                    id="signupPasswordConfirm"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t("auth.login.reenterPassword")}
                    autoComplete="new-password"
                    required
                    disabled={isSubmitting}
                    style={{ paddingRight: "56px" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="password-toggle"
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <i
                      className={`fas ${
                        showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                      }`}
                      aria-hidden="true"
                    ></i>
                    <span className="sr-only">
                      {showConfirmPassword ? t("auth.login.hidePassword") : t("auth.login.showPassword")}
                    </span>
                  </button>
                </div>
              </div>
              <button
                className="auth-button auth-button-primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? t("auth.login.creating") : t("auth.login.createAccount")}
              </button>
              <div className="auth-actions">
                <button
                  type="button"
                  className="auth-button auth-button-secondary auth-button-full"
                  onClick={() => switchMode("signIn")}
                  disabled={isSubmitting}
                >
                  {t("auth.login.backToSignIn")}
                </button>
              </div>
            </form>
          )}

          {mode === "confirm" && (
            <form className="auth-form" onSubmit={handleConfirmSignUp}>
              <div className="form-group">
                <label htmlFor="code">{t("auth.verify.codeLabel")}</label>
                <input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={t("auth.verify.codePlaceholder")}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <button
                className="auth-button auth-button-primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? t("auth.verify.verifying") : t("auth.verify.verify")}
              </button>
              <div className="auth-actions">
                <button
                  type="button"
                  className="auth-button auth-button-secondary"
                  onClick={async () => {
                    setErrorMessage("");
                    try {
                      await resendSignUpCode({ username: email });
                    } catch (e: any) {
                      setErrorMessage(e?.message || t("auth.verify.errors.resendFailed"));
                    }
                  }}
                  disabled={isSubmitting}
                >
                  {t("auth.verify.resendCode")}
                </button>
                <button
                  type="button"
                  className="auth-button auth-button-secondary"
                  onClick={() => switchMode("signIn")}
                  disabled={isSubmitting}
                >
                  {t("auth.login.backToSignIn")}
                </button>
              </div>
            </form>
          )}

          {mode === "reset" && (
            <form className="auth-form" onSubmit={handleStartReset}>
              <div className="form-group">
                <label htmlFor="resetEmail">{t("auth.login.emailLabel")}</label>
                <input
                  id="resetEmail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("auth.login.emailPlaceholder")}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <button
                className="auth-button auth-button-primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? t("auth.login.sendingCode") : t("auth.login.sendResetCode")}
              </button>
              <div className="auth-actions">
                <button
                  type="button"
                  className="auth-button auth-button-secondary auth-button-full"
                  onClick={() => setMode("signIn")}
                  disabled={isSubmitting}
                >
                  {t("auth.login.backToSignIn")}
                </button>
              </div>
            </form>
          )}

          {mode === "resetConfirm" && (
            <form className="auth-form" onSubmit={handleConfirmReset}>
              <div className="form-group">
                <label htmlFor="resetCode">{t("auth.login.resetCode")}</label>
                <input
                  id="resetCode"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={t("auth.verify.codePlaceholder")}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">{t("auth.login.newPassword")}</label>
                <div className="password-wrapper">
                  <input
                    id="newPassword"
                    type={showResetNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t("auth.login.newPasswordPlaceholder")}
                    required
                    disabled={isSubmitting}
                    style={{ paddingRight: "56px" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetNewPassword((v) => !v)}
                    className="password-toggle"
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <i
                      className={`fas ${
                        showResetNewPassword ? "fa-eye-slash" : "fa-eye"
                      }`}
                      aria-hidden="true"
                    ></i>
                    <span className="sr-only">
                      {showResetNewPassword ? t("auth.login.hidePassword") : t("auth.login.showPassword")}
                    </span>
                  </button>
                </div>
              </div>
              <button
                className="auth-button auth-button-primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? t("auth.login.resetting") : t("auth.login.resetPassword")}
              </button>
              <div className="auth-actions">
                <button
                  type="button"
                  className="auth-button auth-button-secondary auth-button-full"
                  onClick={() => switchMode("signIn")}
                  disabled={isSubmitting}
                >
                  {t("auth.login.backToSignIn")}
                </button>
              </div>
            </form>
          )}

          {shouldShowDevMessage && (
            <div
              className="auth-dev-message"
              style={{
                backgroundColor: isDevBypass ? "#d4edda" : "#f8f9fa",
                color: isDevBypass ? "#155724" : "#6c757d",
                padding: "10px",
                borderRadius: "8px",
                marginTop: "16px",
              }}
            >
              <strong>{envLabel} Mode:</strong>{" "}
              {isDevBypass
                ? t("auth.login.devBypassEnabled")
                : t("auth.login.devBypassAvailable")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
